<template>
  <div class="page-block">
    <header-bar>登録情報</header-bar>
    <div class="page-block__heading page-block__heading--withbar">
      <h3 class="page-block__title">
        アカウント情報 -
        <span v-if="isNew">登録</span>
        <span v-else>編集</span>
      </h3>
    </div>
    <div class="page-block__inner">
      <div
        v-if="!isNew"
        class="profile-heading"
        :class="{ 'form--error': uploadErrors }">
        <div
          class="profile-label">
          アカウント画像
          <i
            v-tooltip="'<b>横 512 ピクセル、縦 512 ピクセル未満の画像</b>'"
            class="tooltip-icon"
            style="display: inline;">
          </i>
        </div>
        <div class="profile-heading__wrap">
          <div class="profile-heading__pic">
            <img
              v-if="previewImage"
              :src="previewImage">
            <div
              class="profile-heading__pic--edit"
              @click.prevent="onUploadClicked">
            </div>
          </div>
          <div class="profile-heading__name">
            {{ isBtoB ? item.company.name : item.name }}
          </div>
        </div>
        <p
          v-for="(uploadError, index) in uploadErrors"
          :key="index"
          class="error-text">
          {{ uploadError }}
        </p>
        <p
          v-if="!uploadErrors"
          class="form-item-text--inline">
          横 512 ピクセル、縦 512 ピクセル未満のJPEG/PNG画像
        </p>
        <input
          ref="file"
          type="file"
          style="display:none"
          @change="onFileChanged" />
      </div>
      <div
        v-else>
        <div class="profile-edit__head-text">
          <span>ご登録ありがとうございます。</span>
          <br>アカウント管理情報を登録してください。
        </div>
      </div>
      <alert-view :errors="errors" />
      <div class="profile-info">
        <form
          v-if="item.id"
          :class="{ 'form--error': errors }">
          <div
            class="form-wrap form-wrap--profile"
            :class="{ 'u-no-margin-top': isNew }">
            <div
              v-if="!item.company.is_consumer"
              class="form-group">
              <div class="form-label">
                <label class="control-label">法人名</label>
                <span
                  v-if="isNew"
                  class="req">
                  ＊
                </span>
              </div>
              <div
                v-if="!companyNameIsSaved"
                class="form-item">
                <div
                  class="form-item--with-caution">
                  <input
                    v-model="item.company.name"
                    class="form-control form-control--w400"
                    type="text"
                    placeholder="例）ズーラ株式会社">
                  <span class="form-item-text--inline u-text-color--pink">
                    一度登録された内容は変更できません。
                  </span>
                </div>
                <p
                  v-if="errors['company.name']"
                  class="error-text">
                  {{ errors['company.name'][0] }}
                </p>
              </div>
              <div
                v-else
                class="form-item readonly-row">
                <div class="readonly-value">
                  {{ item.company.name }}
                </div>
                <span class="form-item-text--inline u-text-color--pink">
                  法人名を変更する場合は
                  <router-link
                    class="c-text-link"
                    target="_blank"
                    :to="{ name: 'contact' }">
                    お問い合わせ
                  </router-link>
                  ください。
                </span>
              </div>
            </div>
            <div
              v-if="isBtoB"
              class="form-group">
              <div class="form-label">
                <label class="control-label">代表者名</label>
                <span
                  v-if="isNew"
                  class="req">
                  ＊
                </span>
              </div>
              <div
                v-if="!companyRepresentativeNameIsSaved"
                class="form-item">
                <div class="form-item--with-caution">
                  <input
                    v-model="item.company.representative_name"
                    class="form-control form-control--w400"
                    type="text"
                    placeholder="例）山田太郎">
                  <span class="form-item-text--inline u-text-color--pink">
                    一度登録された内容は変更できません。<br>姓名の順で入力してください。
                  </span>
                </div>
                <p
                  v-if="errors['company.representative_name']"
                  class="error-text">
                  {{ errors['company.representative_name'][0] }}
                </p>
              </div>
              <div
                v-else
                class="form-item readonly-row">
                <div class="readonly-value">
                  {{ item.company.representative_name }}
                </div>
                <span class="form-item-text--inline u-text-color--pink">
                  代表者名を変更する場合は
                  <router-link
                    class="c-text-link"
                    target="_blank"
                    :to="{ name: 'contact' }">
                    お問い合わせ
                  </router-link>
                  ください。
                </span>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="staff">
                  {{ isBtoB ? '担当者名' : 'アカウント管理者氏名' }}
                </label>
                <span class="req">＊</span>
              </div>
              <div class="form-item">
                <input
                  id="staff"
                  v-model="item.company.contact_name"
                  class="form-control"
                  type="text"
                  placeholder="例）山田太郎">
                <span v-if="isBtoC">
                  ※ 必ず正しい氏名を入力してください。<br />
                  ※ 収益を受け取る銀行口座の口座名義と同一の名前を登録してください。
                </span>
                <p
                  v-if="errors['company.contact_name']"
                  class="error-text">
                  {{ errors['company.contact_name'][0] }}
                </p>
              </div>
            </div>
            <div
              v-if="isBtoC"
              class="form-group">
              <div class="form-label">
                <label class="control-label">
                  生年月日
                  <span class="req">＊</span>
                </label>
              </div>
              <div
                class="form-item">
                <date-picker
                  v-model="item.dob"
                  :disabled-date="disabledDates"
                  :editable="false"
                  value-type="format">
                </date-picker>
                <p
                  v-if="errors['dob']"
                  class="error-text">
                  {{ errors['dob'][0] }}
                </p>
              </div>
            </div>
            <div
              v-if="isBtoB"
              class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="tel">電話番号
                </label>
                <span class="req">＊</span>
              </div>
              <div class="form-item">
                <input
                  id="tel"
                  v-model="item.company.telephone"
                  class="form-control"
                  type="text"
                  placeholder="例）0312345678">
                <span class="form-item-text--inline">ハイフンなしの半角数字を記入してください。</span>
                <p
                  v-if="errors['company.telephone']"
                  class="error-text">
                  {{ errors['company.telephone'][0] }}
                </p>
              </div>
            </div>
            <div
              v-if="isBtoC && item.mobile_number"
              class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="mobile">SMS認証用電話番号
                </label>
                <span class="req">＊</span>
              </div>
              <div class="form-item">
                <input
                  id="mobile"
                  v-model="formatMobileNumber"
                  class="form-control form-control--w370"
                  type="text"
                  disabled
                  readonly />
              </div>
              <span
                class="form-item-text--inline">この番号は変更できません。<br />
                変更が必要な場合は<a
                  href="https://narasu.jp/client/contact"
                  target="_blank"
                  style="color: blue;text-decoration: underline;">こちら</a>からお問い合わせください。
              </span>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="zip_1">郵便番号
                </label>
                <span class="req">＊</span>
              </div>
              <div class="form-item">
                <input
                  id="zip_1"
                  v-model="zip1"
                  class="form-control form-control--postal1"
                  type="text"
                  placeholder="123"
                  maxlength="3"> ー
                <input
                  id="zip_2"
                  v-model="zip2"
                  class="form-control form-control--postal2"
                  type="text"
                  placeholder="1234"
                  maxlength="4">
                <a
                  class="c-btn c-btn--postal"
                  :class="{ 'c-btn--off': searchButtonDisabled }"
                  href="#"
                  @click.prevent="onSearchPostalCodeClicked">検索
                  <span class="c-btn__icon-arrow"></span>
                </a>
                <p
                  v-if="errors['company.postal_code']"
                  class="error-text">
                  {{ errors['company.postal_code'][0] }}
                </p>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="pref">都道府県
                </label>
                <span class="req">＊</span>
              </div>
              <div class="form-item">
                <div class="c-select_wrap">
                  <select
                    id="pref"
                    v-model="item.company.prefecture"
                    class="form-control c-select"
                    name="language">
                    <option
                      v-for="prefecture in prefectures"
                      :key="prefecture"
                      :value="prefecture">
                      {{ prefecture }}
                    </option>
                  </select>
                </div>
                <p
                  v-if="errors['company.prefecture']"
                  class="error-text">
                  {{ errors['company.prefecture'][0] }}
                </p>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="address1">市区町村 / 番地
                </label>
                <span class="req">＊</span>
              </div>
              <div class="form-item">
                <input
                  id="address1"
                  v-model="item.company.address1"
                  class="form-control"
                  type="text"
                  placeholder="例）東京都渋谷区道玄坂1-1-1">
                <p
                  v-if="errors['company.address1']"
                  class="error-text">
                  {{ errors['company.address1'][0] }}
                </p>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="address2">建物名
                </label>
              </div>
              <div class="form-item">
                <input
                  id="address2"
                  v-model="item.company.address2"
                  class="form-control"
                  type="text"
                  placeholder="例）江戸ビル1111">
                <p
                  v-if="errors['company.address2']"
                  class="error-text">
                  {{ errors['company.address2'][0] }}
                </p>
              </div>
            </div>
          </div>
          <div class="c-btn-wrap">
            <a
              href="#"
              class="c-btn"
              @click.prevent="onSaveClicked">
              内容を保存
              <span class="c-btn__icon-arrow"></span>
            </a>
            <router-link
              v-if="!isNew"
              :to="{ name: 'profile' }"
              class="c-btn c-btn--back c-btn--back-left">
              戻る
              <span class="c-btn__icon-arrow c-btn__icon-arrow--left"></span>
            </router-link>
            <a
              v-if="isNew"
              href="#"
              class="c-btn c-btn--back c-btn--right"
              @click.prevent="onSkipClicked">あとで設定
              <span class="c-btn__icon-arrow c-btn__icon-arrow--right"></span>
            </a>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker'
import base64ToBinary from '../../../logic/image'
import HeaderBar from '../HeaderBar'
import AlertView from '../AlertView'
import 'vue2-datepicker/index.css'
import 'vue2-datepicker/locale/ja'

export default {
  name: 'ProfileEdit',
  components: {
    DatePicker,
    HeaderBar,
    AlertView,
  },
  props: {
    isNew: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      item: {
        company: {},
      },
      zip1: '',
      zip2: '',
      errors: {},
      prefectures: [
        '北海道',
        '青森県',
        '岩手県',
        '宮城県',
        '秋田県',
        '山形県',
        '福島県',
        '茨城県',
        '栃木県',
        '群馬県',
        '埼玉県',
        '千葉県',
        '東京都',
        '神奈川県',
        '新潟県',
        '富山県',
        '石川県',
        '福井県',
        '山梨県',
        '長野県',
        '岐阜県',
        '静岡県',
        '愛知県',
        '三重県',
        '滋賀県',
        '京都府',
        '大阪府',
        '兵庫県',
        '奈良県',
        '和歌山県',
        '鳥取県',
        '島根県',
        '岡山県',
        '広島県',
        '山口県',
        '徳島県',
        '香川県',
        '愛媛県',
        '高知県',
        '福岡県',
        '佐賀県',
        '長崎県',
        '熊本県',
        '大分県',
        '宮崎県',
        '鹿児島県',
        '沖縄県',
      ],
      previewImage: null,
      validated: false,
      uploadErrors: [],
      companyNameIsSaved: false,
      companyRepresentativeNameIsSaved: false,
    }
  },
  computed: {
    searchButtonDisabled() {
      return (this.zip1.length !== 3 || this.zip2.length !== 4)
    },
    // remove country code from saved mobile number
    formatMobileNumber() {
      const mobile = this.item.mobile_number
      if (mobile) {
        return `0${mobile.replace(/^(\+81)/, '')}`
      }
      return ''
    },
  },
  created() {
    if (this.isNew) {
      if (this.$store.state.auth.name) {
        this.$router.push({ name: 'index' })
        return
      }
      this.$store.commit('hideSidebar')
    }
  },
  mounted() {
    this.fetchItem()
  },
  methods: {
    async fetchItem() {
      const response = await window.axios.get(`/api/client/users/${this.$store.state.auth.id}`)
      this.item = response.data
      this.zip1 = this.item.company.postal_code ? this.item.company.postal_code.substr(0, 3) : ''
      this.zip2 = this.item.company.postal_code ? this.item.company.postal_code.substr(3, 4) : ''
      this.companyNameIsSaved = !!this.item.company.name
      this.companyRepresentativeNameIsSaved = !!this.item.company.representative_name
      if (this.item.avatar_path) {
        this.previewImage = `/images/${this.item.avatar_path}`
      }
    },
    async onSaveClicked() {
      window.scrollTo(0, 0)

      const keys = [
        'name',
        'representative_name',
        'contact_name',
        'telephone',
        'postal_code',
        'prefecture',
        'address1',
        'address2',
      ]
      const params = this.item.company
      params.postal_code = this.zip1 + this.zip2

      const formData = new FormData()
      const config = { headers: { 'content-type': 'multipart/form-data' } }

      if (this.previewImage && this.previewImage.indexOf('/') !== 0) {
        const base64String = this.previewImage.substr(this.previewImage.indexOf(',') + 1) // 先頭に制御文字があるので除去
        formData.append('avatar', base64ToBinary(base64String))
      }
      Object.keys(params)
        .filter(key => (keys.indexOf(key) !== -1))
        .forEach(key => {
          formData.append(`company[${key}]`, params[key] !== null ? params[key] : '')
        })
      formData.append('dob', this.item.dob != null ? this.item.dob : '')

      try {
        if (this.isNew && this.isBtoC) {
          await window.axios.post('/api/client/user/profile-new', formData, config)
        } else {
          await window.axios.post('/api/client/user/profile', formData, config)
        }

        if (this.isNew) {
          await this.$router.push({ name: 'artist-new' })
        } else {
          await this.$router.push({ name: 'profile' })
        }
      } catch ({ response }) {
        const { errors } = response.data
        this.errors = errors
      }
    },
    onSkipClicked() {
      window.scrollTo(0, 0)
      this.$router.push({ name: 'artist-new' })
    },
    // ファイル選択ボタンクリック
    onUploadClicked() {
      this.$refs.file.value = ''
      this.$refs.file.click()
    },
    // ファイル選択時コールバック
    onFileChanged(e) {
      const files = e.target.files || e.dataTransfer.files
      const reader = new FileReader()
      reader.onload = event => {
        this.checkImage(event.target.result)
      }
      reader.readAsDataURL(files[0])
    },
    // 画像のバリデーション
    async checkImage(base64image) {
      this.error = ''
      this.uploadErrors = []
      this.validated = false
      const base64String = base64image.substr(base64image.indexOf(',') + 1) // 先頭に制御文字があるので除去
      const formData = new FormData()
      const config = { headers: { 'content-type': 'multipart/form-data' } }
      formData.append('avatar', base64ToBinary(base64String))

      const response = await window.axios.post('/api/client/image/validate/avatar', formData, config)
      if (response.data.result) {
        this.previewImage = base64image
        this.validated = true
      } else {
        this.uploadErrors = response.data.messages
        this.validated = false
      }
    },
    async onSearchPostalCodeClicked() {
      const response = await window.axios.get('/api/client/postal-code', {
        params: {
          code1: this.zip1,
          code2: this.zip2,
        },
      })
      if (response.data.length > 0) {
        const { address, city, prefecture } = response.data[0]
        this.item.company.prefecture = prefecture
        this.item.company.address1 = `${city}${address}`
      }
    },
    disabledDates(date) {
      const currentDate = new Date()
      const minDate = new Date(1900, 0, 1)
      const maxDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate(),
      )

      // Disable dates before 1900 and future dates
      return date < minDate || date > maxDate
    },
  },
}
</script>

<style >
#tel {
  width: 320px;
}
.profile-heading__pic {
  background: url(../../../../../public/img/common/account_pic.png) no-repeat center center;
  border-radius: 50%;
  overflow: hidden;
  overflow: hidden;
  position: relative;
  background-size: contain;
}
.profile-heading__pic img{
  height: 100%;
}
.input-wrapper input {
  border: none!important;
  padding: 0!important;
}
.readonly-row {
  display: flex;
  justify-content: left;
}
.readonly-value {
  width: 300px
}
input[name="date"] {
  color: black;
}
</style>
